// Shared shape and formatting for the wholesale price sheet (PDF and Excel).
// The data comes from backend/crm/getPriceSheet.

import { wholesalePrice } from './margins'

export interface PriceSheetSize {
  size: string
  sku: string
  price: number | null
  weight_oz: number | null
}

export interface PriceSheetFlavor {
  flavor: string
  tier: string | null
  categories: string[]
  description: string
  available_start: string | null
  available_end: string | null
  photo_url: string | null
  photo_file_id: string | null
  sizes: PriceSheetSize[]
  excluded: boolean
}

export interface PriceSheetTier {
  tier: string
  minimum: number | null
}

export interface PriceSheetData {
  generated_at: string
  tiers: PriceSheetTier[]
  flavors: PriceSheetFlavor[]
}

/** Discounts off MSRP the "Your cost" column can show. */
export const PRICE_SHEET_DISCOUNTS = [0.3, 0.4, 0.5] as const
export type PriceSheetDiscount = (typeof PRICE_SHEET_DISCOUNTS)[number]
export const DEFAULT_PRICE_SHEET_DISCOUNT: PriceSheetDiscount = 0.3

const DISCOUNT_KEY = 'priceSheetDiscount'

/** The last discount picked on this browser, so both price sheet menus agree. */
export function loadPriceSheetDiscount(): PriceSheetDiscount {
  try {
    const raw = Number(window.localStorage.getItem(DISCOUNT_KEY))
    return PRICE_SHEET_DISCOUNTS.find(d => d === raw) ?? DEFAULT_PRICE_SHEET_DISCOUNT
  } catch {
    return DEFAULT_PRICE_SHEET_DISCOUNT
  }
}

export function savePriceSheetDiscount(discount: PriceSheetDiscount): void {
  try {
    window.localStorage.setItem(DISCOUNT_KEY, String(discount))
  } catch {
    // Storage blocked: the choice just isn't remembered.
  }
}

/** "40%" */
export function discountLabel(discount: number): string {
  return `${Math.round(discount * 100)}%`
}

/** Customer's cost at `discount` off MSRP, rounded to the cent. */
export function customerCost(msrp: number | null, discount: number): number | null {
  if (msrp == null) return null
  return Math.round(wholesalePrice(msrp, discount) * 100) / 100
}

export const PRICE_SHEET_COMPANY = "Poppin' West Texas"
export const PRICE_SHEET_ORDER_SITE = 'b2b.poppinwtx.com'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-11-15" -> "Nov 15". Seasonal dates repeat every year, so no year. */
export function monthDay(value: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  if (!match) return ''
  const month = MONTHS[Number(match[2]) - 1]
  return month ? `${month} ${Number(match[3])}` : ''
}

/** "Nov 15 - Dec 31", or '' for a flavor that isn't seasonal. */
export function seasonLabel(flavor: PriceSheetFlavor): string {
  const start = monthDay(flavor.available_start)
  const end = monthDay(flavor.available_end)
  return start && end ? `${start} – ${end}` : ''
}

export function formatPrice(price: number | null): string {
  return price == null ? '—' : `$${price.toFixed(2)}`
}

export function formatWeight(weight: number | null): string {
  if (weight == null) return ''
  return `${Number.isInteger(weight) ? weight : weight.toFixed(1)} oz`
}

export function tierMinimum(data: PriceSheetData, tier: string | null): number | null {
  if (!tier) return null
  return data.tiers.find(t => t.tier === tier)?.minimum ?? null
}

/** "Poppin West Texas Price Sheet 2026-10-15.pdf" */
export function priceSheetFileName(data: PriceSheetData, extension: 'pdf' | 'xlsx'): string {
  const date = /^\d{4}-\d{2}-\d{2}/.exec(data.generated_at)?.[0] ?? ''
  const local = new Date(data.generated_at)
  const day = Number.isNaN(local.getTime())
    ? date
    : `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`
  return `Poppin West Texas Price Sheet ${day}.${extension}`
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Blob -> base64 (no data: prefix), for emailing as an attachment. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}
